/**
 * Typed Notification System
 *
 * WebSocket-only notifications - no client-side echo suppression needed.
 * Server tags events with `_source` (or `source`); the client determines
 * behavior via matrix lookup keyed on that classification.
 */

import type { NotificationSource } from "./common/enums";

// Client-side effective source — classified purely from `_source`/`source`.
export type EffectiveNotificationSource = NotificationSource;

export const EFFECTIVE_SOURCE = {
	LOCAL: "local", // Local UI action (the operator's own action)
	AGENT: "agent", // AI assistant action
	EXTERNAL_USER: "external_user", // Another user's action
	SYSTEM_AGENT: "system_agent", // System/automated action
} as const;

// Operations that trigger notifications (matches WebSocket event types)
export type OperationType =
	| "event_created"
	| "event_updated"
	| "event_cancelled"
	| "event_reinstated"
	| "event_completed"
	| "event_no_show"
	| "conversation_new_message"
	| "conversation_ai_toggled"
	| "conversation_feedback"
	| "vacation_period_updated"
	| "user_active_updated";

// Notification behaviors
export type NotificationBehavior =
	| "TOAST"
	| "DROPDOWN"
	| "TOAST_AND_DROPDOWN"
	| "SILENT";

// Compile-time enforced matrix
export type NotificationMatrix = {
	[O in OperationType]: {
		[S in EffectiveNotificationSource]: NotificationBehavior;
	};
};

// Runtime matrix
// LOCAL → TOAST only (the operator's own action; they know what they did)
// AGENT/EXTERNAL_USER → TOAST + DROPDOWN (external action you should notice)
// SYSTEM_AGENT → SILENT (background operations)
export const NOTIFICATION_MATRIX: NotificationMatrix = {
	event_created: {
		local: "TOAST",
		agent: "TOAST_AND_DROPDOWN",
		external_user: "TOAST_AND_DROPDOWN",
		system_agent: "SILENT",
	},
	event_updated: {
		local: "TOAST",
		agent: "TOAST_AND_DROPDOWN",
		external_user: "TOAST_AND_DROPDOWN",
		system_agent: "SILENT",
	},
	event_cancelled: {
		local: "TOAST",
		agent: "TOAST_AND_DROPDOWN",
		external_user: "TOAST_AND_DROPDOWN",
		system_agent: "SILENT",
	},
	event_reinstated: {
		local: "TOAST",
		agent: "TOAST_AND_DROPDOWN",
		external_user: "TOAST_AND_DROPDOWN",
		system_agent: "SILENT",
	},
	event_completed: {
		local: "TOAST",
		agent: "TOAST_AND_DROPDOWN",
		external_user: "TOAST_AND_DROPDOWN",
		system_agent: "SILENT",
	},
	event_no_show: {
		local: "TOAST",
		agent: "TOAST_AND_DROPDOWN",
		external_user: "TOAST_AND_DROPDOWN",
		system_agent: "SILENT",
	},
	conversation_new_message: {
		local: "SILENT", // You just typed this, no feedback needed
		agent: "TOAST_AND_DROPDOWN",
		external_user: "TOAST_AND_DROPDOWN",
		system_agent: "SILENT",
	},
	vacation_period_updated: {
		local: "TOAST",
		agent: "TOAST_AND_DROPDOWN",
		external_user: "TOAST_AND_DROPDOWN",
		system_agent: "SILENT",
	},
	user_active_updated: {
		local: "TOAST",
		agent: "TOAST_AND_DROPDOWN",
		external_user: "TOAST_AND_DROPDOWN",
		system_agent: "SILENT",
	},
	// High-visibility: AI handing off to a human is operationally important
	// regardless of who triggered it. Even an operator's own toggle should land
	// in the dropdown so it survives a tab switch — losing this notification
	// means a customer sits unanswered.
	conversation_ai_toggled: {
		local: "TOAST_AND_DROPDOWN",
		agent: "TOAST_AND_DROPDOWN",
		external_user: "TOAST_AND_DROPDOWN",
		system_agent: "TOAST_AND_DROPDOWN",
	},
	// Customer-volunteered feedback. Always agent-sourced (the AI calls the
	// saveFeedback tool); other source values exist defensively.
	conversation_feedback: {
		local: "TOAST_AND_DROPDOWN",
		agent: "TOAST_AND_DROPDOWN",
		external_user: "TOAST_AND_DROPDOWN",
		system_agent: "TOAST_AND_DROPDOWN",
	},
};

/**
 * Get the notification behavior for an operation and source.
 * Returns SILENT for unknown operations.
 */
export function getNotificationBehavior(
	operation: OperationType,
	source: EffectiveNotificationSource
): NotificationBehavior {
	return NOTIFICATION_MATRIX[operation]?.[source] ?? "SILENT";
}
